// src/components/BlogCard.tsx
import React, { useState, useEffect } from 'react';
import { BlogPost } from '../types/blog';
import { useBlogPreviewImage } from '../hooks/useBlogPreviewImage';
import { getTagsForBlogPost } from '../utils/tagExtraction';

interface BlogCardProps {
  post: BlogPost;
  onClick: (post: BlogPost) => void;
}

const BlogCard: React.FC<BlogCardProps> = ({ post, onClick }) => {
  const { image, loading } = useBlogPreviewImage(post.s3Url);
  const [displayTags, setDisplayTags] = useState<string[]>(post.tags || []);
  const [tagsLoading, setTagsLoading] = useState(false);

  useEffect(() => {
    const loadTags = async () => {
      setTagsLoading(true);
      try {
        const extractedTags = await getTagsForBlogPost(post);
        setDisplayTags(extractedTags);
      } catch (error) {
        console.error('Error loading tags for post:', post.id, error);
        setDisplayTags(post.tags || ['market', 'analysis', 'daily']);
      } finally {
        setTagsLoading(false);
      }
    };

    loadTags();
  }, [post]);

  const formatTitle = (title: string) => {
    return title
      .replace('nifty-today-', '')
      .replace(/-/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div 
      className="bg-gray-900 rounded-lg shadow-lg overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
      onClick={() => onClick(post)}
    >
      {/* Image Section */}
      <div className="relative h-48 overflow-hidden">
        {loading ? (
          <div className="w-full h-full bg-gray-700 animate-pulse flex items-center justify-center">
            <div className="text-gray-400">Loading...</div>
          </div>
        ) : (
          <img 
            src={image || '/random_images/maxim-hopman-fiXLQXAhCfk-unsplash.jpg'}
            alt="Market Analysis"
            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
            onError={(e) => {
              // Fallback to a different random image if the primary image fails
              const target = e.target as HTMLImageElement;
              const fallbackImages = [
                '/random_images/anne-nygard-tcJ6sJTtTWI-unsplash.jpg',
                '/random_images/hans-eiskonen-wn57cSQ7VzI-unsplash.jpg',
                '/random_images/jamie-street-VP4WmibxvcY-unsplash.jpg',
                '/random_images/maxim-hopman-fiXLQXAhCfk-unsplash.jpg'
              ];
              const randomIndex = Math.floor(Math.random() * fallbackImages.length);
              target.src = fallbackImages[randomIndex];
            }}
          />
        )}
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
        
        
      </div>

      {/* Content Section */}
      <div className="p-6">
        <h3 className="text-xl font-semibold mb-3 text-white line-clamp-2">
          {formatTitle(post.title)}
        </h3>
        
        {post.excerpt && (
          <p className="text-gray-300 mb-4 text-sm line-clamp-2">
            {post.excerpt}
          </p>
        )}

        {/* Tags */}
        <div className="flex flex-wrap gap-2">
          {tagsLoading ? (
            // Loading skeleton for tags
            <>
              <div className="bg-gray-700 animate-pulse text-xs px-2 py-1 rounded-full h-6 w-16"></div>
              <div className="bg-gray-700 animate-pulse text-xs px-2 py-1 rounded-full h-6 w-20"></div>
              <div className="bg-gray-700 animate-pulse text-xs px-2 py-1 rounded-full h-6 w-14"></div>
            </>
          ) : (
            <>
              {displayTags.slice(0, 3).map(tag => (
                <span 
                  key={tag}
                  className="bg-[#9E7C47]/20 text-[#F9F1A9] text-xs px-2 py-1 rounded-full border border-[#9E7C47]/30"
                >
                  {tag}
                </span>
              ))}
              {displayTags.length > 3 && (
                <span className="text-gray-400 text-xs">
                  +{displayTags.length - 3} more
                </span>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogCard;